import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  TruckIcon,
  ClockIcon,
  ChevronRightIcon,
  ChatBubbleLeftRightIcon,
  FunnelIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import supabase from './utils/supabaseClient';
import { SERVICES, formatBRL } from './utils/pricing';

const STATUS_COLORS = {
  aguardando_pagamento: '#F59E0B',
  pago:                 '#7D47E4',
  buscando_entregador:  '#42169D',
  aceito:               '#6C39CF',
  em_coleta:            '#6C39CF',
  coletado:             '#6C39CF',
  em_rota:              '#6C39CF',
  entregue:             '#22C55E',
  cancelado:            '#EF4444',
  criado:               '#878AA8',
};

const STATUS_META = {
  criado:               { label: 'Criado',          bg: 'bg-ink-100',     text: 'text-ink-600'    },
  aguardando_pagamento: { label: 'Aguardando pgto', bg: 'bg-accent-100',  text: 'text-accent-700' },
  pago:                 { label: 'Pago',            bg: 'bg-brand-100',   text: 'text-brand-700'  },
  buscando_entregador:  { label: 'Buscando',        bg: 'bg-brand-100',   text: 'text-brand-700'  },
  aceito:               { label: 'Aceito',          bg: 'bg-brand-100',   text: 'text-brand-700'  },
  em_coleta:            { label: 'Na coleta',       bg: 'bg-brand-100',   text: 'text-brand-700'  },
  coletado:             { label: 'Coletado',        bg: 'bg-brand-100',   text: 'text-brand-700'  },
  em_rota:              { label: 'A caminho',       bg: 'bg-brand-100',   text: 'text-brand-700'  },
  entregue:             { label: 'Entregue',        bg: 'bg-green-100',   text: 'text-green-700'  },
  cancelado:            { label: 'Cancelado',       bg: 'bg-red-100',     text: 'text-red-700'    },
};

const STATUS_OPTIONS = Object.keys(STATUS_META).map((v) => ({ value: v, label: STATUS_META[v].label }));

const FILTERS = [
  { value: '',                     label: 'Todos' },
  { value: 'aguardando_pagamento', label: 'Aguardando pgto' },
  { value: 'buscando_entregador',  label: 'Sem entregador' },
  { value: 'em_rota',              label: 'Em andamento' },
  { value: 'entregue',             label: 'Entregues' },
  { value: 'cancelado',            label: 'Cancelados' },
];

const fmt = (iso) => iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

const Badge = ({ status }) => {
  const m = STATUS_META[status] ?? STATUS_META.criado;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  );
};

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-2">{title}</p>
    <div className="rounded-xl border border-ink-100 overflow-hidden">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-2 px-4 py-2.5 border-b border-ink-50 last:border-0 text-sm">
    <span className="text-ink-500 shrink-0">{label}</span>
    <span className="text-ink-900 font-medium text-right break-words">{value || '—'}</span>
  </div>
);

const AdminPedidosTab = () => {
  const [pedidos, setPedidos]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]         = useState('');
  const [selected, setSelected]     = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editEntregadorId, setEditEntregadorId] = useState('');
  const [editNotes, setEditNotes]   = useState('');
  const [saving, setSaving]         = useState(false);
  const [entregadores, setEntregadores] = useState([]);

  // Carregar entregadores aprovados (para atribuição manual)
  useEffect(() => {
    supabase
      .from('entregadores')
      .select('id, primeiro_nome, sobrenome, tipo_veiculo')
      .eq('status', 'aprovado')
      .order('primeiro_nome', { ascending: true })
      .then(({ data }) => setEntregadores(data ?? []));
  }, []);

  // Carregar pedidos
  useEffect(() => {
    setLoading(true);
    supabase
      .from('pedidos')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500)
      .then(({ data, error }) => {
        setLoading(false);
        if (error) { toast.error('Erro ao carregar pedidos.'); return; }
        setPedidos(data ?? []);
      });
  }, [refreshKey]);

  // Realtime: refresh ao mudar pedidos
  useEffect(() => {
    const ch = supabase
      .channel('admin-pedidos')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pedidos' }, () => {
        setRefreshKey((k) => k + 1);
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  // ESC fecha painel
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') setSelected(null); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, []);

  const selectedId = selected?.id;
  useEffect(() => {
    if (selected) {
      setEditStatus(selected.status);
      setEditEntregadorId(selected.entregador_id ?? '');
      setEditNotes(selected.notas_internas ?? '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Stats
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayPedidos = pedidos.filter(p => new Date(p.created_at) >= today);
    const revenueToday = todayPedidos
      .filter(p => p.status === 'entregue' || p.pagamento_status === 'aprovado')
      .reduce((s, p) => s + Number(p.preco || 0), 0);
    const inProgress = pedidos.filter(p =>
      ['pago','buscando_entregador','aceito','em_coleta','coletado','em_rota'].includes(p.status)
    ).length;
    const waitingDriver = pedidos.filter(p => p.status === 'buscando_entregador').length;
    return {
      todayCount: todayPedidos.length,
      revenueToday,
      inProgress,
      waitingDriver,
    };
  }, [pedidos]);

  // Pedidos por dia · últimos 30 dias
  const series30 = useMemo(() => {
    const dias = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      dias.push({
        date: d,
        label: d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        total: 0,
        entregues: 0,
        cancelados: 0,
      });
    }
    pedidos.forEach((p) => {
      const d = new Date(p.created_at);
      d.setHours(0, 0, 0, 0);
      const idx = dias.findIndex((x) => x.date.getTime() === d.getTime());
      if (idx >= 0) {
        dias[idx].total += 1;
        if (p.status === 'entregue')  dias[idx].entregues += 1;
        if (p.status === 'cancelado') dias[idx].cancelados += 1;
      }
    });
    return dias.map(({ label, total, entregues, cancelados }) => ({
      label, total, entregues, cancelados,
    }));
  }, [pedidos]);

  // Distribuição por status (donut)
  const statusDistribution = useMemo(() => {
    const counts = {};
    pedidos.forEach((p) => {
      counts[p.status] = (counts[p.status] || 0) + 1;
    });
    return Object.entries(counts).map(([status, count]) => ({
      status,
      label: status.replace(/_/g, ' '),
      count,
      color: STATUS_COLORS[status] || '#878AA8',
    }));
  }, [pedidos]);

  const filtered = useMemo(() => {
    let list = pedidos;
    if (statusFilter) list = list.filter(p => p.status === statusFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(p =>
        [p.codigo_rastreio, p.cliente_nome, p.cliente_email, p.cliente_whatsapp]
          .some(v => v && String(v).toLowerCase().includes(s))
      );
    }
    return list;
  }, [pedidos, statusFilter, search]);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    const updates = {
      status: editStatus,
      entregador_id: editEntregadorId === '' ? null : Number(editEntregadorId),
      notas_internas: editNotes,
    };
    // Timestamps automáticos quando admin força um status
    const now = new Date().toISOString();
    if (editStatus === 'pago' && !selected.pago_at)         updates.pago_at = now;
    if (editStatus === 'aceito' && !selected.aceito_at)     updates.aceito_at = now;
    if (editStatus === 'coletado' && !selected.coletado_at) updates.coletado_at = now;
    if (editStatus === 'entregue' && !selected.entregue_at) updates.entregue_at = now;
    if (editStatus === 'cancelado' && !selected.cancelado_at) updates.cancelado_at = now;

    const { error } = await supabase.from('pedidos').update(updates).eq('id', selected.id);
    setSaving(false);
    if (error) { toast.error('Erro ao salvar.'); console.error(error); return; }
    const updated = { ...selected, ...updates };
    setPedidos(arr => arr.map(p => p.id === selected.id ? updated : p));
    setSelected(updated);
    toast.success('Pedido atualizado.');
  };

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Pedidos hoje',     value: stats.todayCount,     color: 'text-ink-900',   Icon: ClockIcon },
          { label: 'Receita hoje',     value: formatBRL(stats.revenueToday), color: 'text-green-700', Icon: CurrencyDollarIcon },
          { label: 'Em andamento',     value: stats.inProgress,     color: 'text-brand-700', Icon: TruckIcon },
          { label: 'Sem entregador',   value: stats.waitingDriver,  color: 'text-accent-600', Icon: MagnifyingGlassIcon },
        ].map(({ label, value, color, Icon }) => (
          <div key={label} className="card p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-ink-500">{label}</p>
              <Icon className={`h-4 w-4 ${color}`} />
            </div>
            <p className={`text-3xl font-display font-bold mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Charts: pedidos/dia + distribuição por status */}
      {pedidos.length > 0 && (
        <div className="grid lg:grid-cols-3 gap-4 mb-8">
          <div className="card p-5 lg:col-span-2">
            <p className="font-display font-bold text-ink-900 mb-3">Pedidos · últimos 30 dias</p>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={series30} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E9F0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#878AA8' }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: '#878AA8' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E8E9F0' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="entregues"  stackId="a" fill="#22C55E" name="Entregues" radius={[0,0,0,0]} />
                  <Bar dataKey="cancelados" stackId="a" fill="#EF4444" name="Cancelados" radius={[0,0,0,0]} />
                  <Bar dataKey="total"      fill="#7D47E4" name="Total criados" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card p-5">
            <p className="font-display font-bold text-ink-900 mb-3">Por status</p>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={statusDistribution}
                    dataKey="count"
                    nameKey="label"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={2}
                  >
                    {statusDistribution.map((s, i) => (
                      <Cell key={i} fill={s.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, n) => [v, String(n).replace(/_/g, ' ')]}
                    contentStyle={{ borderRadius: 12, border: '1px solid #E8E9F0' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-2 space-y-1 text-xs">
              {statusDistribution.map((s) => (
                <li key={s.status} className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-ink-600">
                    <span className="h-2 w-2 rounded-full inline-block" style={{ background: s.color }} />
                    {s.label}
                  </span>
                  <span className="font-bold text-ink-900">{s.count}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="card p-4 mb-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <FunnelIcon className="h-4 w-4 text-ink-400 shrink-0" />
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition
                ${statusFilter === f.value
                  ? 'bg-brand-gradient text-white shadow-glow'
                  : 'bg-ink-100 text-ink-600 hover:bg-ink-200'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative sm:ml-auto w-full sm:w-64">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400 pointer-events-none" />
          <input
            className="input pl-9 text-sm"
            type="search"
            placeholder="Código, cliente, e-mail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          onClick={() => setRefreshKey(k => k + 1)}
          className="btn-ghost text-xs gap-1.5"
          title="Atualizar"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Tabela */}
      <div className="card overflow-hidden">
        {loading && pedidos.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 rounded-full border-4 border-brand-200 border-t-brand-600 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-ink-400">
            <TruckIcon className="h-10 w-10 mx-auto mb-3" />
            <p className="text-sm font-medium">Nenhum pedido encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 bg-ink-50/70">
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Código</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Cliente</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink-500 hidden sm:table-cell">Serviço</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-ink-500">Valor</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink-500 hidden lg:table-cell">Quando</th>
                  <th className="w-8 px-2" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => setSelected(p)}
                    className="border-b border-ink-50 hover:bg-brand-50/40 cursor-pointer transition"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-brand-700">{p.codigo_rastreio}</td>
                    <td className="px-4 py-3 text-ink-900 font-medium">{p.cliente_nome || '—'}</td>
                    <td className="px-4 py-3 text-ink-500 hidden sm:table-cell">{(SERVICES[p.servico_id] ?? SERVICES.frete_pequeno).name.replace('Fretz ', '')}</td>
                    <td className="px-4 py-3"><Badge status={p.status} /></td>
                    <td className="px-4 py-3 text-right font-bold text-ink-900">{formatBRL(p.preco)}</td>
                    <td className="px-4 py-3 text-ink-400 hidden lg:table-cell">{fmt(p.created_at)}</td>
                    <td className="px-2 py-3 text-ink-300"><ChevronRightIcon className="h-4 w-4" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-ink-400 text-right px-4 py-3 border-t border-ink-50">
              {filtered.length} pedido{filtered.length !== 1 ? 's' : ''}
              {statusFilter || search ? ` (de ${pedidos.length} total)` : ''}
            </p>
          </div>
        )}
      </div>

      {/* Detalhe */}
      {selected && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="flex-1 bg-ink-900/40 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          />
          <aside className="w-full max-w-lg bg-white shadow-xl flex flex-col overflow-hidden">
            <div className="flex items-start justify-between px-6 py-4 border-b border-ink-100 shrink-0 gap-3">
              <div className="min-w-0">
                <p className="text-xs text-ink-400 font-bold uppercase">Pedido</p>
                <p className="font-mono font-bold text-brand-700 truncate">{selected.codigo_rastreio}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge status={selected.status} />
                <button
                  onClick={() => setSelected(null)}
                  className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-400"
                  aria-label="Fechar"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

              <Section title="Cliente">
                <Row label="Nome"      value={selected.cliente_nome} />
                <Row label="E-mail"    value={selected.cliente_email} />
                <Row label="WhatsApp"  value={selected.cliente_whatsapp} />
              </Section>

              {selected.cliente_whatsapp && (
                <a
                  href={`https://wa.me/55${(selected.cliente_whatsapp || '').replace(/\D/g, '')}?text=${encodeURIComponent(`Olá! Sobre o pedido ${selected.codigo_rastreio}.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-sm gap-1.5 w-full"
                >
                  <ChatBubbleLeftRightIcon className="h-4 w-4" />
                  Falar com cliente no WhatsApp
                </a>
              )}

              <Section title="Trajeto">
                <Row label="Coleta"    value={selected.endereco_coleta} />
                <Row label="Entrega"   value={selected.endereco_entrega} />
                <Row label="Item"      value={selected.descricao_item} />
                <Row label="Distância" value={`${Number(selected.distancia_km).toFixed(1)} km`} />
                <Row label="Tempo"     value={`${Math.round(selected.tempo_estimado_min)} min`} />
                <Row label="Retorno"   value={selected.retorno ? 'Sim' : 'Não'} />
              </Section>

              <Section title="Serviço & valor">
                <Row label="Serviço" value={(SERVICES[selected.servico_id] ?? SERVICES.frete_pequeno).name} />
                <Row label="Valor"   value={formatBRL(selected.preco)} />
              </Section>

              <Section title="Pagamento">
                <Row label="Status"    value={selected.pagamento_status} />
                <Row label="Provider"  value={selected.pagamento_provider} />
                <Row label="ID"        value={selected.pagamento_id} />
              </Section>

              <Section title="Histórico">
                <Row label="Criado"        value={fmt(selected.created_at)} />
                <Row label="Pago"          value={fmt(selected.pago_at)} />
                <Row label="Aceito"        value={fmt(selected.aceito_at)} />
                <Row label="Coletado"      value={fmt(selected.coletado_at)} />
                <Row label="Entregue"      value={fmt(selected.entregue_at)} />
                <Row label="Cancelado"     value={fmt(selected.cancelado_at)} />
              </Section>

              <Section title="Atualizar pedido">
                <div className="p-4 space-y-3">
                  <div>
                    <label className="label" htmlFor="ed-status">Status</label>
                    <select id="ed-status" className="input" value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}>
                      {STATUS_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label" htmlFor="ed-ent">Entregador</label>
                    <select id="ed-ent" className="input" value={editEntregadorId}
                      onChange={(e) => setEditEntregadorId(e.target.value)}>
                      <option value="">— Sem entregador —</option>
                      {entregadores.map(e => (
                        <option key={e.id} value={e.id}>
                          {e.primeiro_nome} {e.sobrenome} ({e.tipo_veiculo})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label" htmlFor="ed-notes">Notas internas</label>
                    <textarea id="ed-notes" className="input min-h-[80px] resize-y"
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Observações internas (não visíveis ao cliente)…" />
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="btn-primary w-full gap-2"
                  >
                    {saving ? 'Salvando…' : (<><CheckCircleIcon className="h-4 w-4" /> Salvar alterações</>)}
                  </button>
                </div>
              </Section>

              <p className="text-xs text-ink-400 text-right pb-2">
                Cadastro em {fmtDate(selected.created_at)}
              </p>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default AdminPedidosTab;
